/**
 * Qui parle, vignette par vignette (le live) : l'écran pose UNE source au-dessus de sa page (`SpeakingContext`), et
 * chaque vignette demande seulement si SON participant parle. Quand quelqu'un se met à parler ou se tait, seules les
 * vignettes de cette personne se redessinent — jamais la page, ni la salle.
 *
 * Sans source (une story, un écran qui n'en veut pas), la réponse est `null` : ni barres ni liseré.
 */
import { createContext, useCallback, useContext, useSyncExternalStore } from "react"

export type SpeakingSource = {
    /** `personId` (l'id de sa vignette) parle en ce moment. */
    isSpeaking: (personId: string) => boolean
    /** Prévient `onChange` quand `personId` se met à parler ou se tait ; rend de quoi arrêter. */
    subscribe: (personId: string, onChange: () => void) => () => void
}

export const SpeakingContext = createContext<SpeakingSource | null>(null)

const NOTHING = () => () => undefined

/** `personId` parle ; `false` sans participant ; `null` sans source (rien à montrer). */
export function useSpeaking(personId: string | undefined): boolean | null {
    const source = useContext(SpeakingContext)
    const subscribe = useCallback(
        (onChange: () => void) =>
            source == null || personId == null ? NOTHING() : source.subscribe(personId, onChange),
        [source, personId]
    )
    const read = () => (source == null ? null : personId != null && source.isSpeaking(personId))
    return useSyncExternalStore(subscribe, read, read)
}
